import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Users, Trash2, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const ManageTeamDialog = ({ subsidiary, open, onClose, onUpdate }) => {
  const [assignedMembers, setAssignedMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && subsidiary) {
      fetchAssignedMembers();
      fetchAvailableUsers();
    }
  }, [open, subsidiary]);

  const fetchAssignedMembers = async () => {
    if (!subsidiary) return;
    
    try {
      const response = await axios.get(
        `${API}/subsidiaries/${subsidiary.id}/members`
      );
      setAssignedMembers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching assigned members:", error);
      toast.error("Failed to load team members");
      setAssignedMembers([]);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await axios.get(`${API}/company-users`);
      // API returns {users: [], total: 0} format
      setAvailableUsers(response.data?.users || []);
    } catch (error) {
      console.error("Error fetching available users:", error);
      toast.error("Failed to load users");
      setAvailableUsers([]);
    }
  };

  const handleAssignUser = async () => {
    if (!selectedUser) {
      toast.error("Please select a user");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/subsidiaries/${subsidiary.id}/members`,
        { user_id: selectedUser }
      );
      toast.success("Team member assigned successfully");
      setSelectedUser("");
      fetchAssignedMembers();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error assigning team member:", error);
      toast.error(error.response?.data?.detail || "Failed to assign team member");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this team member?")) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(
        `${API}/subsidiaries/${subsidiary.id}/members/${userId}`
      );
      toast.success("Team member removed");
      fetchAssignedMembers();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error removing team member:", error);
      toast.error("Failed to remove team member");
    } finally {
      setLoading(false);
    }
  };

  // Filter out already assigned users
  const unassignedUsers = availableUsers.filter(
    user => !assignedMembers.some(m => m.user_id === user.id)
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Manage Team - {subsidiary?.name}
          </DialogTitle>
          <DialogDescription>
            Assign team members to this subsidiary. Only assigned members will appear in owner dropdowns for this subsidiary's plans.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Assigned Members List */}
          <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Assigned Team Members ({assignedMembers.length})
            </h4>
            {assignedMembers.length === 0 ? (
              <div className="text-center py-8 border border-dashed rounded-lg">
                <Users className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No team members assigned yet</p>
                <p className="text-xs text-slate-400 mt-1">Assign team members below</p>
              </div>
            ) : (
              <div className="space-y-2">
                {assignedMembers.map((member) => (
                  <div
                    key={member.user_id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-sm">{member.full_name}</p>
                      <p className="text-xs text-slate-600">{member.email}</p>
                      {member.role_name && (
                        <p className="text-xs text-blue-600 mt-1">Role: {member.role_name}</p>
                      )}
                      {member.is_primary && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded mt-1 inline-block">
                          Primary
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveUser(member.user_id)}
                      disabled={loading}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Assign New Member */}
          <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Assign New Member
            </h4>
            <div className="flex gap-2">
              <Select value={selectedUser} onValueChange={setSelectedUser} disabled={loading}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  {unassignedUsers.length === 0 ? (
                    <div className="p-3 text-sm text-slate-500 text-center">
                      All users are assigned
                    </div>
                  ) : (
                    unassignedUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.full_name} ({user.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <Button 
                onClick={handleAssignUser} 
                disabled={!selectedUser || loading}
              >
                Assign
              </Button>
            </div>
            {unassignedUsers.length === 0 && assignedMembers.length > 0 && (
              <p className="text-xs text-slate-500 mt-2">
                All company users have been assigned to this subsidiary
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ManageTeamDialog;
